"use client";

import React from "react";
import {
  MapCanvasProvider,
  MapCanvasProviderProps
} from "./components/map/contexts/MapCanvasContext";
import { ViewCanvas } from "./components/ViewCanvas";
import { ViewSVG } from "./components/ViewSVG/ViewSVG";
import { LegendBridgeData, ViewSVGLegendHandle } from "./types";

export interface MapViewProps
  extends Pick<
    MapCanvasProviderProps,
    | "isPrivacyMode"
    | "authenticatedUser"
    | "highlightResource"
    | "onZoneLinkClick"
    | "initialPosition"
    | "interactiveZones"
    | "value"
    | "isDark"
    | "resourceColors"
    | "zoneColors"
  > {
  miniMapWidth?: number;
  miniMapHeight?: number;
  useSVG?: boolean;
  style?: React.SVGProps<SVGSVGElement>["style"];
  children?: React.ReactNode;
  /** Optional controls drawn in map coordinates above the SVG shapes. */
  svgOverlay?: React.ReactNode;
  /** Hide the zone-type legend overlay — set this when the map editor
   *  is open so the legend doesn't sit above the editor UI. */
  hideZoneLegend?: boolean;
  /** Forwarded to ZoneLegend — see its `bottomOffset` doc. */
  legendBottomOffset?: number | string;
  /** Forwarded to ViewSVG — see its own doc. */
  bridgeZoneLegend?: boolean;
  onLegendDataChange?: (data: LegendBridgeData) => void;
  legendControlRef?: React.RefObject<ViewSVGLegendHandle | null>;
  /** Forwarded to ViewSVG: crop the rendering to one region of the map. */
  focusBounds?: { x: number; y: number; width: number; height: number };
}

export const MapView = React.forwardRef<SVGSVGElement | null, MapViewProps>(
  (props, ref) => {
    const {
      value,
      initialPosition,
      interactiveZones,
      highlightResource,
      onZoneLinkClick,
      miniMapHeight,
      miniMapWidth,
      useSVG = false,
      style,
      children,
      svgOverlay,
      isDark,
      hideZoneLegend,
      legendBottomOffset,
      bridgeZoneLegend,
      onLegendDataChange,
      legendControlRef,
      isPrivacyMode,
      authenticatedUser,
      resourceColors,
      zoneColors,
      focusBounds
    } = props;
    return (
      <MapCanvasProvider
        value={value}
        initialPosition={initialPosition}
        interactiveZones={interactiveZones}
        highlightResource={highlightResource}
        onZoneLinkClick={onZoneLinkClick}
        isViewMode
        isDark={isDark}
        isPrivacyMode={isPrivacyMode}
        authenticatedUser={authenticatedUser}
        resourceColors={resourceColors}
        zoneColors={zoneColors}
      >
        {useSVG ? (
          <ViewSVG
            ref={ref}
            svgOverlay={svgOverlay}
            style={style}
            hideZoneLegend={hideZoneLegend}
            legendBottomOffset={legendBottomOffset}
            bridgeZoneLegend={bridgeZoneLegend}
            onLegendDataChange={onLegendDataChange}
            legendControlRef={legendControlRef}
            focusBounds={focusBounds}
          />
        ) : (
          <ViewCanvas
            miniMapWidth={miniMapWidth}
            miniMapHeight={miniMapHeight}
          />
        )}
        {children}
      </MapCanvasProvider>
    );
  }
);
